import { useEffect, useRef, useState } from "react";

import { SharedItemClient } from "@/Common/Api/SharedItem/SharedItemClient";
import { ButtonControl } from "@/Common/Components/Buttons/ButtonControl";
import { usePortalCommunicationMessageForm } from "@/Common/Components/SharedItem/Hooks/usePortalCommunicationMessageForm";
import { PortalCommunicationMessageForm } from "@/Common/Components/SharedItem/PortalCommunicationMessageForm";
import { displayToast, MessageToast } from "@/Common/Components/Toast/Toast";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { Person } from "@/Common/Models/Person/Person";
import { PortalLinkedItemType } from "@/Common/Models/Portal/Enums/PortalLinkedItemType";
import { SharedItemActionType } from "@/Common/Models/Portal/SharedItem/SharedItemActionType";
import { SharedItemMessage } from "@/Common/Models/Portal/SharedItem/SharedItemMessage";

import styles from "./PortalCommunicationCreateMessage.module.scss";

interface PortalCommunicationCreateMessageProps {
    onClose: () => void;
    onSentCallback?: () => void;
    onMessageSent?: (sharedItemId: string, messages: SharedItemMessage[]) => void;
    usedDynamicHeight?: boolean;
    patient?: Person;
}

export function PortalCommunicationCreateMessage(props: Readonly<PortalCommunicationCreateMessageProps>): JSX.Element {
    const { getText } = useTranslation();
    const [isSendInProgress, setIsSendInProgress] = useState<boolean>(false);

    const {
        formState,
        isFormValid,
        onSetFormState
    } = usePortalCommunicationMessageForm(!props.patient);

    const handleSendClick = async (): Promise<void> => {
        setIsSendInProgress(true);

        const { messageText, patient, priority, subject, canPatientReplyWithMessage } = formState;

        const newSharedItemMessage = SharedItemMessage.createNewSharedItemMessage(messageText, priority);
        try {
            const response = await SharedItemClient.syncUpsertSharedItemMessage(
                [newSharedItemMessage],
                props.patient?.ContactId ?? patient.ContactId,
                null,
                PortalLinkedItemType.None,
                null,
                subject,
                canPatientReplyWithMessage
            );

            if (response) {
                displayToast(MessageToast.Success, getText("SharedItem_MessageSentLabel"));

                if (props.onMessageSent) {
                    props.onMessageSent(response.SharedItem.SharedItemId, response.Messages);
                    return;
                }

                props.onClose();
            }
        }
        finally {
            if (isMounted.current) {
                setIsSendInProgress(false);
            }
        }
    };

    const isMounted = useRef(true);

    useEffect(() => () => {
        isMounted.current = false;
    }, []);

    const isSendButtonDisabled = !isFormValid || isSendInProgress;

    return (
        <div className={`${styles.mainContainer} ${props.usedDynamicHeight ? styles.dynamicHeight : ""}`} data-cy="portal-communication-create-message-container">
            <div className={`${styles.header} myle-page-header`}>
                <div className={styles.title}>
                    {getText("CommunicationsLabel")}
                </div>
                <div className={styles.subTitle}>
                    {getText("SharedItem_NewPortalCommunicationLabel")}
                </div>
            </div>
            <PortalCommunicationMessageForm
                currentPatient={props.patient}
                componentId="CommunicationTab_CreateNewMessageForm"
                actionType={SharedItemActionType.New}
                containerClass={styles.newMessageContainer}
                formContainerClass={styles.formContainer}
                onNewPortalCommunicationMessageChange={newState => onSetFormState({
                    ...formState,
                    ...newState
                })}
            />
            <div className={`${styles.footer} ws-footer`}>
                <div className={styles.actionButtons}>
                    <ButtonControl
                        onClick={() => props.onClose()}
                        label={getText("CancelLabel")}
                        variant="neutral"
                        dataCy="portal-communication-create-message-close"
                    />
                    <ButtonControl
                        onClick={() => handleSendClick()}
                        label={getText("SendLabel")}
                        variant="brand"
                        disabled={isSendButtonDisabled}
                        dataCy="portal-communication-create-message-send"
                    />
                </div>
            </div>
        </div>
    );
}
